import { useCallback } from 'react';

import { useModal, useToastr } from '@farmshare/ui-components';

import { useCustomerMergeMutation } from '../../../lib/graphql';

import { MergeWizardCompareModalStep } from '../components/merge-wizard-compare-modal-step';
import { MergeWizardReviewModalStep } from '../components/merge-wizard-review-modal-step';
import { MergeWizardSelectModalStep } from '../components/merge-wizard-select-modal-step';
import { type SelectOption } from '../components/merge-wizard-select-step';
import {
  candidateLabel,
  type CustomerSummary,
} from '../components/merge-wizard-types';
import {
  emptySources,
  MERGE_WIZARD_STEPS,
  type MergeWizardData,
  mergeVariablesOf,
  mergeWizardGates,
} from './merge-wizard-data';
import { userFacingError } from 'lib/user-facing-error';

export * from './merge-wizard-data';

export interface MergeWizardModalOptions {
  vendorId: string | undefined;
  initiatingCustomer: CustomerSummary;
  onCompleted: (result: { affectedRecordCount: number }) => void;
}

/**
 * Returns a function that opens the 3-step customer merge wizard.
 *
 * Step navigation, the progress bar, Back/Next and fullscreen-on-mobile all
 * come from the shared multi-step modal; this hook supplies the step bodies,
 * the per-step "can advance" rules, and the merge itself.
 * @returns A callback that opens the wizard.
 */
export function useMergeWizardModal() {
  const { multiStep } = useModal();
  const { push: pushToast } = useToastr();
  const [runMerge] = useCustomerMergeMutation();

  return useCallback(
    ({
      vendorId,
      initiatingCustomer,
      onCompleted,
    }: MergeWizardModalOptions) => {
      const initiatingOption: SelectOption = {
        _id: initiatingCustomer._id,
        label: candidateLabel(initiatingCustomer),
      };

      multiStep<MergeWizardData>({
        analyticsName: 'merge_customers',
        title: 'Merge customers',
        size: 'md',
        showCancelOnFirstStep: true,
        // Opening the wizard builds a fresh config, and the renderer seeds its
        // state from it — so there is no "reset on open" effect to carry over
        // from the old dialog, which stayed mounted between openings.
        initialData: {
          selected: [initiatingOption],
          masterId: '',
          fieldSources: emptySources(''),
          showAll: false,
          consent: false,
          candidates: [],
          candidatesLoading: false,
          preview: null,
          previewLoading: false,
        },
        steps: [
          {
            title: MERGE_WIZARD_STEPS[0],
            analyticsKey: 'select',
            body: (data, actions) => (
              <MergeWizardSelectModalStep
                data={data}
                actions={actions}
                vendorId={vendorId}
                initiatingId={initiatingCustomer._id}
              />
            ),
            canGoNext: mergeWizardGates.select,
          },
          {
            title: MERGE_WIZARD_STEPS[1],
            analyticsKey: 'compare',
            body: (data, actions) => (
              <MergeWizardCompareModalStep
                data={data}
                actions={actions}
                initiatingId={initiatingCustomer._id}
                vendorId={vendorId}
              />
            ),
            canGoNext: mergeWizardGates.compare,
          },
          {
            title: MERGE_WIZARD_STEPS[2],
            analyticsKey: 'review',
            body: (data, actions) => (
              <MergeWizardReviewModalStep
                data={data}
                actions={actions}
                vendorId={vendorId}
              />
            ),
            nextButtonText: 'Merge',
            canGoNext: mergeWizardGates.review,
          },
        ],
        onComplete: async (data) => {
          if (!vendorId) return;
          // Throws if the primary or a field source is not among the selected
          // records, so a merge into a de-selected customer is never sent.
          const { data: result } = await runMerge({
            variables: { ...mergeVariablesOf(data), vendorId },
          });
          onCompleted({
            affectedRecordCount:
              result?.customerMerge?.affectedRecordCount ?? 0,
          });
        },
        // A throw from onComplete leaves the wizard open on Review, so the user
        // can retry or back out. The old dialog showed the reason in an inline
        // Alert; the multi-step renderer has no slot for one, so it goes to a
        // toast — which on a phone is the more visible of the two anyway.
        onError: (error, context) => {
          if (context !== 'complete') return;
          pushToast({
            title: "Couldn't merge these customers",
            body: userFacingError(
              error,
              'Nothing was merged. Try again, or go back and check your picks.',
            ),
            severity: 'error',
          });
        },
      });
    },
    [multiStep, pushToast, runMerge],
  );
}
